import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, FormikHelpers, FormikErrors } from 'formik';
import { Redirect } from 'react-router-dom';
import { Colors } from 'src/common';
import { merchantLoginRoute, merchantLogin, paymentsDashboardRoute } from 'src/routing/routes';

import {
  FloatingInput,
  ShapesDecorativeBorder,
  DotLoader,
  OtpInput,
  ButtonLink,
} from 'src/components';
import TealiumEvents from 'src/helpers/tealium-events';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SelfServiceFormBackground } from '../self-service-form-background/self-service-form-background';
import { FormContainer } from '../form-container/form-container';
import { ImageCache } from 'src/components/image-cache/image-cache';
import {
  CactusImage,
  CashAdvanceApproved,
  CashAdvanceCongrats,
  CashAdvanceRoadSign,
  ComingSoonLabel,
  Error,
  FallenIceCream,
  FallingBags,
  HandCash,
  InputErrorIcon,
  RocketImage,
  TapPay,
  TickedCheckboxPortal,
  UntickedCheckboxPortal,
  CloseIcon,
} from 'src/assets/images';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Button } from '@vfs-digital-channels/ns-react-components';
import {
  LoginTypeLookUpResponse,
  merchantAuth,
  merchantKey,
  MerchantLoginRequestBody,
  MerchantLoginResponse,
  OtpErrorResponse,
  OtpSuccessResponse,
  validateMerchantOpt,
} from 'src/services/ni-login';
import { InputField } from 'src/features/common';
import { Error as TechnicalErrorIcon } from 'src/assets/images';

type LoginFormValues = {
  merchantId: string;
  otp: string;
  businessType?: string;
};

export default function LoginV2() {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const handleTechnicalError = useTechnicalErrorHandler();
  const [redirectToRoute, setRedirectToRoute] = useState('');
  enum LoginTypeRedirect {
    otpLogin = 'NI',
    merchantLogin = 'TR',
  }
  const [loginType, setLoginType] = useState<LoginTypeLookUpResponse | null>(null);
  const [otpAttempt, setOtpAttempt] = useState<OtpErrorResponse | null>(null);
  const [disableMerchantId, setDisableMerchantId] = useState<boolean>(false);
  const [showBusinessType, setShowBusinessType] = useState<boolean>(false);
  const [disableOtp, setDisableOtp] = useState<boolean>(false);
  const [merchantOtpValidationSuccess, setMerchantOtpValidationSuccess] =
    useState<OtpSuccessResponse | null>(null);
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  const [otpSuccess, setOtpSuccess] = useState(false);

  const handleItemSelect = (item) => {
    setSelectedResponseId(item.responseId);
  };

  const [initialValues, setInitialValues] = useState<LoginFormValues>({
    merchantId: '',
    otp: '',
  });

  const topOfPage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loginType) {
      setDisableMerchantId(true);
      setInitialValues({
        merchantId: loginType.user ?? '',
        otp: '',
      });
    }
  }, [loginType]);

  useEffect(() => {
    const storedResponse = sessionStorage.getItem('loginTypeResponse');
    if (storedResponse) {
      setLoginType(JSON.parse(storedResponse));
    }

    if (MiniAppEvents.isInMiniApp()) {
      const siteTitle = document.getElementById('siteTitle');
      if (siteTitle) {
        siteTitle.innerText = 'Business Portal';
      }
    }
  }, []);

  useEffect(() => {
    if (inMiniProgram) {
      MiniAppEvents.sendMessage({
        messageType: 'get_msisdn',
      });

      MiniAppEvents.listenForMessage((observer) => {
        const message = MiniAppEvents.readMessage();
        if (message.type === 'get_msisdn') {
          if (message.data) {
            const newValues = { ...initialValues, merchantId: message.data };
            setInitialValues(newValues);
          }
          observer.disconnect();
        }
      });
    }
  }, [inMiniProgram]);

  useEffect(() => {
    TealiumEvents.loginPageLoadNewStyle();
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const formContent = () => {
    return (
      <section className="self-service-login-form">
        <ImageCache
          images={[
            TapPay,
            CactusImage,
            CashAdvanceApproved,
            FallingBags,
            HandCash,
            CashAdvanceCongrats,
            CashAdvanceRoadSign,
            ComingSoonLabel,
            Error,
            FallenIceCream,
            InputErrorIcon,
            RocketImage,
            UntickedCheckboxPortal,
            TickedCheckboxPortal,
            CloseIcon,
          ]}
        />
        <Formik<LoginFormValues>
          initialValues={initialValues}
          validate={validateLoginForm}
          onSubmit={submitLoginForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          {({ isSubmitting, values }) => (
            <FormContainer>
              <Form>
                <div className="pt-10 text-center">
                  <div className="pt-5 pb-4 text-4xl text-grey-600" ref={topOfPage}>
                    Log in to your account
                  </div>
                </div>
                <div className="pb-8 space-y-8">
                  <div className="space-y-5">
                    {!merchantOtpValidationSuccess && (
                      <>
                        <Field
                          name="merchantId"
                          type="text"
                          placeholder="Enter your Merchant ID or cell number"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          disabled={disableMerchantId}
                          fieldType="standard"
                          className="top-field"
                          spacing="has-margin-top-5"
                          automationIdPrefix="Login_Input_MerchantID"
                          autoFocus
                        />
                        <Field
                          name="otp"
                          component={MiniAppFocusWrapper}
                          innerComponent={OtpInput}
                          key={1}
                          shouldAutoFocus={true}
                          isDisabled={disableOtp}
                          numInputs={6}
                        />
                      </>
                    )}

                    {otpAttempt !== null && (
                      <div className="flex flex-col items-center pt-2">
                        <p className="text-base ">
                          <img className="w-8 h-8" src={TechnicalErrorIcon} />
                          &nbsp;
                          {otpAttempt.otpAttempts === otpAttempt.otpMaxAttempts
                            ? 'OTP attempts exceeded. Please try again.'
                            : ' Incorrect OTP - Try again'}
                        </p>
                      </div>
                    )}
                    {!showBusinessType && otpAttempt === null && (
                      <div className="flex flex-col items-center pt-2">
                        <p className="text-base font-bold text-grey-600">We’ve sent you and OTP</p>
                        <p className="text-base text-grey-600">
                          Didn’t receive an OTP? &nbsp;
                          <ButtonLink
                            automationid="Login_Link_First_Time_Login"
                            className="font-bold"
                            onClick={() => {}}
                            disabled={isSubmitting || disableOtp}
                          >
                            Resend
                          </ButtonLink>
                        </p>
                      </div>
                    )}

                    {merchantOtpValidationSuccess && showBusinessType && (
                      <Field
                        name="businessType"
                        type="text"
                        placeholder="Select business"
                        items={merchantOtpValidationSuccess.merchants}
                        getItemDisplayValue={(item) => item.businessName}
                        component={InputField}
                        onItemSelect={handleItemSelect}
                        fieldType="select"
                        isDark={false}
                      />
                    )}
                  </div>
                  <div className="space-y-[10px]">
                    <Button
                      className="w-full !py-6 !text-base !font-medium !bg-red-600 disabled:!bg-grey-100"
                      type="submit"
                      disabled={isSubmitting || !isFormValid(values, disableOtp)}
                    >
                      {isSubmitting ? <DotLoader color={Colors.BlueGray4} /> : 'Continue'}
                    </Button>
                    <Button
                      className="w-full !py-6  !text-base text-black !font-medium rounded-lg border border-grey-100 !bg-white-50"
                      onClick={() => handleBackButton()}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              </Form>
            </FormContainer>
          )}
        </Formik>
      </section>
    );
  };

  return (
    <div className="broker-login">
      <SelfServiceFormBackground className="">
        <ShapesDecorativeBorder>{formContent()}</ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );

  function handleBackButton() {
    setRedirectToRoute(merchantLoginRoute);
    sessionStorage.removeItem(merchantKey.merchantLookUpRespondeKey);
  }

  async function submitLoginForm(values: LoginFormValues, helpers: FormikHelpers<LoginFormValues>) {
    const { setSubmitting, resetForm } = helpers;
    const params = {
      ...values,
      showToastOnError: false,
      suppressTealiumEvent: true,
    };
    if (!otpSuccess) {
      const response = await validateMerchantOpt(loginType?.requestId ?? '', params.otp);

      if (response) {
        if ('type' in response) {
          const errorResponse = response as OtpErrorResponse;
          resetForm();
          setOtpAttempt(errorResponse);

          if (errorResponse.otpAttempts === errorResponse.otpMaxAttempts) {
            setDisableOtp(true);
            setTimeout(() => {
              handleBackButton();
            }, 2000);
          }
        } else {
          const successResponse = response as OtpSuccessResponse;

          const isMerchantTR = successResponse?.merchants.find(
            (merchant) => merchant.psp === LoginTypeRedirect.merchantLogin,
          );

          const isMerchantNI = successResponse?.merchants.find(
            (merchant) => merchant.psp === LoginTypeRedirect.otpLogin,
          );

          if (successResponse.merchants.length === 1) {
            const requestBody: MerchantLoginRequestBody = {
              merchantEnrollment: false,
              username: successResponse?.merchants[0].merchantNumber,
              password: '',
              findMerchantRequestId: loginType?.requestId ?? '',
              findMerchantResponseId: successResponse?.merchants[0].responseId,
            };
            try {
              const loginResponse = await merchantAuth(requestBody);
              if (loginResponse) {
                const niLoginResponse = loginResponse as MerchantLoginResponse;
                sessionStorage.setItem('niToken', niLoginResponse.result.token);
                setRedirectToRoute(paymentsDashboardRoute);
              }
            } catch {
              // @ts-ignore
              handleTechnicalError('unrecognized error received in response to login POST request');
            }
          } else {
            if (isMerchantNI) {
              setShowBusinessType(true);
            }

            if (isMerchantTR) {
              setRedirectToRoute(merchantLogin);
            }
          }

          setOtpSuccess(true);

          setMerchantOtpValidationSuccess(successResponse);
          setSubmitting(false);
          setDisableOtp(true);
        }
      }
    }

    if (otpSuccess) {
      const findMerchant = merchantOtpValidationSuccess?.merchants.find(
        (merchant) => merchant.responseId === selectedResponseId,
      );

      if (findMerchant) {
        const requestBody: MerchantLoginRequestBody = {
          merchantEnrollment: false,
          username: findMerchant?.merchantNumber,
          password: '',
          findMerchantRequestId: loginType?.requestId ?? '',
          findMerchantResponseId: findMerchant?.responseId,
        };
        try {
          const loginResponse = await merchantAuth(requestBody);
          if (loginResponse) {
            const niLoginResponse = loginResponse as MerchantLoginResponse;
            sessionStorage.setItem('niToken', niLoginResponse.result.token);
            setRedirectToRoute(paymentsDashboardRoute);
          }
        } catch {
          // @ts-ignore
          handleTechnicalError('unrecognized error received in response to login POST request');
        }
      }
    }
  }
}

const isFormValid = (values, isOtpValid) => {
  const { merchantId, otp, businessType } = values;

  const baseValidation = merchantId && otp;

  if (isOtpValid) {
    return baseValidation && businessType;
  }

  return baseValidation;
};
function validateLoginForm(values) {
  const errors: FormikErrors<LoginFormValues> = {};
  if (!values.merchantId) {
    errors.merchantId = 'Your Merchant ID or cell number is incorrect';
  }

  if (!values.otp) {
    errors.otp = 'Your otp is incorrect ';
  }

  return errors;
}
